/**
 * CRTZ Cart Drawer - Shopify Section Rendering API
 * Clean, standard implementation
 */

(function() {
  'use strict';

  window.CRTZ = window.CRTZ || {};

  CRTZ.CartDrawer = class {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-overlay]');
      this.sectionId = this.drawer?.dataset.sectionId || 'cart-drawer-ajax';

      this.routes = {
        cartAdd: `${routes.cart_add_url}.js`,
        cartChange: `${routes.cart_change_url}.js`,
        cartUpdate: `${routes.cart_update_url}.js`,
        cart: '/cart.js'
      };

      this.selectors = {
        drawer: '[data-cart-drawer]',
        overlay: '[data-cart-overlay]',
        toggle: '[data-cart-toggle]',
        close: '[data-cart-close]',
        content: '[data-cart-drawer-content]',
        items: '[data-cart-items]',
        footer: '[data-cart-footer]',
        item: '[data-cart-item]',
        quantityMinus: '[data-quantity-minus]',
        quantityPlus: '[data-quantity-plus]',
        quantityValue: '[data-quantity-value]',
        itemCount: '[data-cart-item-count]',
        subtotal: '[data-cart-subtotal]',
        linePrice: '[data-line-price]',
        checkout: '[data-cart-checkout]',
        terms: '[data-cart-terms]',
        empty: '[data-cart-empty]'
      };

      this.state = {
        isOpen: false,
        isUpdating: false
      };

      this.init();
    }

    init() {
      if (!this.drawer) return;

      this.bindEvents();
    }

    bindEvents() {
      // Toggle buttons (open)
      document.querySelectorAll(this.selectors.toggle).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close button
      const closeBtn = document.querySelector(this.selectors.close);
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.state.isOpen) {
          this.close();
        }
      });

      // Event delegation for quantity buttons and checkout
      this.drawer.addEventListener('click', (e) => {
        const minusBtn = e.target.closest(this.selectors.quantityMinus);
        const plusBtn = e.target.closest(this.selectors.quantityPlus);
        const checkoutBtn = e.target.closest(this.selectors.checkout);

        if (minusBtn) {
          e.preventDefault();
          this.updateQuantity(minusBtn, -1);
        } else if (plusBtn) {
          e.preventDefault();
          this.updateQuantity(plusBtn, 1);
        } else if (checkoutBtn) {
          this.handleCheckout(e);
        }
      });
    }

    open() {
      if (!this.drawer) return;

      this.drawer.classList.add('crtz-cart-drawer--open');
      if (this.overlay) {
        this.overlay.classList.add('crtz-cart-drawer__overlay--visible');
      }
      document.body.style.overflow = 'hidden';
      document.body.classList.add('cart-drawer-open');
      this.state.isOpen = true;

      // Refresh content when opening
      this.render();
    }

    close() {
      if (!this.drawer) return;

      this.drawer.classList.remove('crtz-cart-drawer--open');
      if (this.overlay) {
        this.overlay.classList.remove('crtz-cart-drawer__overlay--visible');
      }
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');
      this.state.isOpen = false;
    }

    async updateQuantity(button, change) {
      if (this.state.isUpdating) return;

      const item = button.closest(this.selectors.item);
      if (!item) return;

      const key = item.dataset.key;
      const quantityEl = item.querySelector(this.selectors.quantityValue);
      const currentQty = parseInt(quantityEl?.textContent || '0');
      const newQuantity = currentQty + change;

      // If quantity becomes 0 or less, remove the item
      if (newQuantity <= 0) {
        await this.removeItem(key);
      } else {
        await this.changeItemQuantity(key, newQuantity);
      }
    }

    async changeItemQuantity(key, quantity) {
      if (this.state.isUpdating) return;
      this.state.isUpdating = true;

      // Show loading state on item
      const item = document.querySelector(`[data-cart-item][data-key="${key}"]`);
      if (item) item.style.opacity = '0.5';

      try {
        const response = await fetch(this.routes.cartChange, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Cart update failed');

        const cart = await response.json();
        await this.render(cart);

      } catch (error) {
        console.error('[CRTZ] Cart update error:', error);
        alert('Error updating cart. Please try again.');
      } finally {
        this.state.isUpdating = false;
        if (item) item.style.opacity = '';
      }
    }

    async removeItem(key) {
      if (this.state.isUpdating) return;
      this.state.isUpdating = true;

      // Show loading state on item
      const item = document.querySelector(`[data-cart-item][data-key="${key}"]`);
      if (item) item.style.opacity = '0.5';

      try {
        const response = await fetch(this.routes.cartChange, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        if (!response.ok) throw new Error('Cart remove failed');

        const cart = await response.json();
        await this.render(cart);

        // Close drawer if cart is empty
        if (cart.item_count === 0) {
          setTimeout(() => this.close(), 300);
        }

      } catch (error) {
        console.error('[CRTZ] Cart remove error:', error);
        alert('Error removing item. Please try again.');
      } finally {
        this.state.isUpdating = false;
      }
    }

    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch(this.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity })
        });

        const data = await response.json();

        if (data.status === 422) {
          throw new Error(data.description || 'Could not add item to cart');
        }

        // Get updated cart and render
        const cartResponse = await fetch(this.routes.cart);
        const cart = await cartResponse.json();

        await this.render(cart);
        this.open();

        return { success: true, cart };

      } catch (error) {
        console.error('[CRTZ] Cart add error:', error);
        alert(error.message || 'Error adding to cart. Please try again.');
        return { success: false, error };
      }
    }

    async render(cartData = null) {
      try {
        // Fetch cart data from Shopify API if not provided
        let cart = cartData;
        if (!cart) {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            cart = await cartResponse.json();
          }
        }

        // Use Section Rendering API to get updated cart HTML
        const response = await fetch(`/?section_id=${this.sectionId}`);
        if (!response.ok) throw new Error('Failed to fetch cart section');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newContent = doc.querySelector('[data-cart-drawer-content]');
        const currentContent = this.drawer.querySelector('[data-cart-drawer-content]');

        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }

        // Update item count from cart data
        const itemCount = cart?.item_count ?? 0;
        this.updateHeaderCount(itemCount);

      } catch (error) {
        console.error('[CRTZ] Cart render error:', error);
      }
    }

    /**
     * Refresh cart data and re-render
     * Call this when cart changes (add/remove/update)
     */
    async refresh(cartData = null) {
      await this.render(cartData);
    }

    updateHeaderCount(count) {
      // Update all cart count badges
      document.querySelectorAll(this.selectors.itemCount).forEach(el => {
        el.textContent = `${count} ITEMS`;
      });

      // Update mobile header cart count
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
      });

      document.querySelectorAll('.crtz-header__cart-count').forEach(el => {
        el.textContent = `(${count})`;
      });
    }

    handleCheckout(e) {
      const termsCheckbox = this.drawer.querySelector(this.selectors.terms);

      if (termsCheckbox && !termsCheckbox.checked) {
        e.preventDefault();
        alert('Please agree to the terms and conditions');
        return false;
      }

      // Proceed to checkout
      window.location.href = '/checkout';
    }
  };

  /**
   * Cart Page Handler
   * Handles quantity updates on the main cart page
   */
  CRTZ.CartPage = class {
    constructor() {
      this.form = document.querySelector('.crtz-cart-page__form');
      if (!this.form) return;

      this.routes = {
        cartChange: `${routes.cart_change_url}.js`,
        cart: '/cart.js'
      };

      this.state = { isUpdating: false };
      this.init();
    }

    init() {
      this.bindEvents();
    }

    bindEvents() {
      // Event delegation for quantity buttons
      this.form.addEventListener('click', (e) => {
        const minusBtn = e.target.closest('[data-quantity-minus]');
        const plusBtn = e.target.closest('[data-quantity-plus]');

        if (minusBtn) {
          e.preventDefault();
          this.updateQuantity(minusBtn, -1);
        } else if (plusBtn) {
          e.preventDefault();
          this.updateQuantity(plusBtn, 1);
        }
      });
    }

    async updateQuantity(button, change) {
      if (this.state.isUpdating) return;

      const item = button.closest('[data-cart-item]');
      if (!item) return;

      const key = item.dataset.key;
      const quantityEl = item.querySelector('[data-quantity-value]');
      const currentQty = parseInt(quantityEl?.textContent || '0');
      const newQuantity = currentQty + change;

      if (newQuantity <= 0) {
        await this.removeItem(key);
      } else {
        await this.changeItemQuantity(key, newQuantity, quantityEl);
      }
    }

    async changeItemQuantity(key, quantity, quantityEl) {
      if (this.state.isUpdating) return;
      this.state.isUpdating = true;

      try {
        const response = await fetch(this.routes.cartChange, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Cart update failed');

        const cart = await response.json();

        // Update UI
        if (quantityEl) quantityEl.textContent = quantity;
        this.updateSubtotal(cart.total_price);
        this.updateHeaderCount(cart.item_count);

        // Update line price for this item
        const item = document.querySelector(`[data-cart-item][data-key="${key}"]`);
        if (item) {
          const cartItem = cart.items.find(i => i.key === key);
          if (cartItem) {
            const priceEl = item.querySelector('[data-line-price]');
            if (priceEl) {
              priceEl.textContent = this.formatMoney(cartItem.final_line_price);
            }
          }
        }

      } catch (error) {
        console.error('[CRTZ] Cart page update error:', error);
        alert('Error updating cart. Please refresh and try again.');
      } finally {
        this.state.isUpdating = false;
      }
    }

    async removeItem(key) {
      if (this.state.isUpdating) return;
      this.state.isUpdating = true;

      try {
        const response = await fetch(this.routes.cartChange, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        if (!response.ok) throw new Error('Cart remove failed');

        const cart = await response.json();

        // Remove item from DOM with animation
        const item = document.querySelector(`[data-cart-item][data-key="${key}"]`);
        if (item) {
          item.style.opacity = '0';
          item.style.transition = 'opacity 0.3s ease';
          setTimeout(() => item.remove(), 300);
        }

        this.updateSubtotal(cart.total_price);
        this.updateHeaderCount(cart.item_count);

        // If cart is empty, reload to show empty state
        if (cart.item_count === 0) {
          setTimeout(() => window.location.reload(), 300);
        }

      } catch (error) {
        console.error('[CRTZ] Cart page remove error:', error);
        alert('Error removing item. Please refresh and try again.');
      } finally {
        this.state.isUpdating = false;
      }
    }

    updateSubtotal(cents) {
      const subtotalEl = document.querySelector('[data-cart-subtotal]');
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cents);
      }
    }

    updateHeaderCount(count) {
      document.querySelectorAll('.crtz-header__cart-count').forEach(el => {
        el.textContent = `(${count})`;
      });
    }

    formatMoney(cents) {
      if (typeof cents === 'undefined' || cents === null) return '£0.00';
      const value = (cents / 100).toFixed(2);
      return '£' + value;
    }
  };

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    CRTZ.cartDrawer = new CRTZ.CartDrawer();
    CRTZ.cartPage = new CRTZ.CartPage();
  });

})();
